import { registerDestructor } from '@ember/destroyable';
import Modifier from 'ember-modifier';

const RADIO_TYPE = 'radio';

/**
 * Radios group by shared `name` *and* shared form owner, so the members found here are exactly the
 * ones the browser treats as one control.
 *
 * Both halves of that rule are needed. Searching the form when there is one keeps the group from
 * spilling into the rest of the page, and matching on form owner keeps a form-less group from
 * claiming same-named radios that belong to a form — a plain document search would find those too,
 * and rewriting their `tabindex` would leave that form's group with the wrong stop, or none.
 * @param {HTMLInputElement} radio a member of the group
 * @return {Array<HTMLInputElement>} the group's members in document order
 */
function groupMembers(radio) {
  let root = radio.form ?? radio.getRootNode();
  let selector = `input[type="${RADIO_TYPE}"][name="${CSS.escape(radio.name)}"]`;

  return [...root.querySelectorAll(selector)].filter(member => member.form === radio.form);
}

/**
 * Gives the group a single sequential tab stop. Only an enabled radio is eligible, since a
 * disabled one cannot take focus and would strand the group outside Tab order. The checked
 * option owns the stop; an untouched group still needs a way in, so it falls back to the first
 * enabled option. A wholly disabled group gets no stop, which is correct.
 * @param {Array<HTMLInputElement>} members the group's members
 */
function applyTabStop(members) {
  let focusable = members.filter(radio => !radio.disabled);
  let tabStop = focusable.find(radio => radio.checked) ?? focusable[0];

  for (let radio of members) {
    radio.tabIndex = radio === tabStop ? 0 : -1;
  }
}

/**
 * Maintains the single sequential tab stop for the radio group the modified input belongs to.
 *
 * Browsers already give a radio group one tab stop, but only while its radios carry no explicit
 * `tabindex`. SPDS has to set one so macOS sequential focus reaches radios at all when the system
 * "Keyboard navigation" setting is off, which opts the group out of that native handling and makes
 * the stop ours to maintain.
 *
 * The stop is always read from the live DOM, never derived from component arguments. Clicking a
 * radio mutates `checked` and fires `change` without re-rendering, so an argument-derived value
 * goes stale immediately, and an uncontrolled group has no argument to read at all. Only the DOM
 * stays accurate for both.
 *
 * What the arguments are for is knowing *when* to look. Two things move the stop:
 *
 * 1. A user selecting a radio, which fires `change`. Every member listens, and any one of them
 *    repairs the whole group — that is what covers the radio that was just deselected, which never
 *    receives a `change` event of its own.
 * 2. A controlled `checked` or `disabled` re-rendering, which mutates the live state silently.
 *    `Spds::Navigation::Tabs` applying `@activeTab` works this way. Callers pass those values so
 *    the render re-runs `modify`, which is the only signal available for this case.
 */
export default class RadioTabStop extends Modifier {
  element = null;

  cleanup = () => {
    if (!this.element) return;

    let radio = this.element;
    let survivors = groupMembers(radio).filter(member => member !== radio);

    radio.removeEventListener('change', this.syncGroup);
    this.element = null;

    // A group that loses the member holding its tab stop would otherwise drop out of Tab order.
    if (survivors.length) applyTabStop(survivors);
  };

  /**
   * @param {HTMLInputElement} element the radio this modifier is attached to
   * @param {Array<unknown>} controlledState the caller's `checked` and `disabled` values
   */
  modify = (element, controlledState) => {
    // Autotracking subscribes this hook to whatever it reads, and nothing below reads the caller's
    // values — the stop comes from live DOM so uncontrolled groups stay correct. This read is what
    // makes a controlled change, which arrives without a `change` event, reach `syncGroup`.
    controlledState.join();

    if (!this.element) {
      if (element.type !== RADIO_TYPE || !element.name) return;

      this.element = element;
      element.addEventListener('change', this.syncGroup);

      registerDestructor(this, this.cleanup);
    }

    this.syncGroup();
  };

  syncGroup = () => {
    applyTabStop(groupMembers(this.element));
  };
}
